<template>
  <!-- howler는 DOM이 필요없다. 컴포넌트 루트만 유지 -->
  <div class="audio-player"></div>
</template>

<script>
import { Howl, Howler } from "howler";
import { resolveResourceUrl } from "@/constants";

const AUDIO_VOLUME = 0.2;
const FADE_MS = 120; // duration 컷 시 짧은 페이드아웃

// 확장자 → howler format 힌트 (대문자/mp4 대비)
function extOf(schema) {
  const name = (schema && (schema.targetFileName || schema.targetKey)) || "";
  const m = String(name)
    .toLowerCase()
    .match(/\.([a-z0-9]+)(?:\?.*)?$/);
  return m ? [m[1]] : undefined;
}

export default {
  created() {
    // Vue 반응성에서 제외 (Howl 인스턴스는 순수 참조로만 보관)
    this._musicHowl = null;
    this._sfxHowls = new Set();
  },
  beforeDestroy() {
    this.stopAll();
  },
  methods: {
    // cmd: { targetRef, duration?, argSchema1: { targetFileName|targetKey, volume?, offset? } }
    executeAudioCmd(cmd) {
      try {
        const { targetRef, duration } = cmd;
        const schema = cmd.argSchema1 || {};
        const { targetFileName, targetKey } = schema;
        if (!targetFileName && !targetKey) return;

        const isMusic = targetRef === "audioMusicPlayer";
        const url = resolveResourceUrl(schema, isMusic ? "musics" : "sounds");
        if (!url) return;

        const volume = typeof schema.volume === "number" ? schema.volume : AUDIO_VOLUME;
        const offsetSec = schema.offset ? schema.offset / 1000 : 0;

        if (isMusic) {
          this._playMusic(url, { volume, offsetSec, duration, format: extOf(schema) });
        } else {
          this._playSfx(url, { volume, offsetSec, duration, format: extOf(schema) });
        }
      } catch (error) {
        console.error("E_AUDIO_CMD", error);
      }
    },

    _playMusic(url, { volume, offsetSec, duration, format }) {
      // 배경음악은 단일 트랙: 이전 것 정리 후 교체
      this._disposeMusic();
      const howl = new Howl({ src: [url], volume, html5: true, format });
      this._musicHowl = howl;
      this._attachPlayback(howl, { offsetSec, duration, isMusic: true });
      howl.play();
    },

    _playSfx(url, { volume, offsetSec, duration, format }) {
      // 효과음은 N개 동시재생: 호출마다 새 Howl
      const howl = new Howl({ src: [url], volume, format });
      this._sfxHowls.add(howl);
      const cleanup = () => {
        this._sfxHowls.delete(howl);
        try {
          howl.unload();
        } catch (e) {
          /* noop */
        }
      };
      howl.once("end", cleanup);
      howl.once("loaderror", cleanup);
      howl.once("playerror", cleanup);
      this._attachPlayback(howl, { offsetSec, duration });
      howl.play();
    },

    // offset seek + duration 컷(fade→stop) 공통 처리
    _attachPlayback(howl, { offsetSec, duration, isMusic }) {
      if (offsetSec > 0) {
        howl.once("play", (id) => howl.seek(offsetSec, id));
      }
      if (duration && duration > 0) {
        const timer = setTimeout(() => {
          if (!howl.playing()) return;
          const v = howl.volume();
          howl.fade(v, 0, FADE_MS);
          setTimeout(() => {
            if (isMusic) {
              this._disposeMusic();
            } else {
              this._sfxHowls.delete(howl);
              try {
                howl.stop();
                howl.unload();
              } catch (e) {
                /* noop */
              }
            }
          }, FADE_MS + 20);
        }, duration);
        // Howl 정리 시 타이머도 정리되도록 참조 보관
        howl._durationTimer = timer;
      }
    },

    _disposeMusic() {
      const howl = this._musicHowl;
      this._musicHowl = null;
      if (!howl) return;
      try {
        if (howl._durationTimer) clearTimeout(howl._durationTimer);
        howl.stop();
        howl.unload();
      } catch (e) {
        /* noop */
      }
    },

    stopMusic() {
      this._disposeMusic();
    },

    // 전역 뮤트(현재 재생 중 + 이후 생성될 Howl 모두). on-air 오디오 mute 토글용.
    setMuted(muted) {
      Howler.mute(!!muted);
    },

    stopAll() {
      this._disposeMusic();
      this._sfxHowls.forEach((howl) => {
        try {
          if (howl._durationTimer) clearTimeout(howl._durationTimer);
          howl.stop();
          howl.unload();
        } catch (e) {
          /* noop */
        }
      });
      this._sfxHowls.clear();
    },
  },
};
</script>

<style></style>
